import {
    Box,
    Breadcrumbs,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    Grid,
    TextField,
    Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { NumericFormat } from "react-number-format";
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../api';
import DidNotLoad from "../../components/DidNotLoad";
import Loading from "../../components/Loading";
import TextEditor from "../../components/TextEditor";
import { parseFormattedMoneyToCents, resolvePublicMediaUrl } from '../../helpers/formatters';

/** API guarda medidas em centésimos de cm (valor em cm × 100), igual ao cadastro. */
function measuresFromApiToForm(m) {
    const empty = { width: '', height: '', length: '' };
    if (!m || typeof m !== 'object') {
        return empty;
    }
    const toCm = (key) => {
        const v = m[key];
        if (v === '' || v == null) {
            return '';
        }
        const n = Number(v);
        if (!Number.isFinite(n)) {
            return '';
        }
        return n / 100;
    };
    return {
        width: toCm('width'),
        height: toCm('height'),
        length: toCm('length'),
    };
}

function EditProductPage() {
    const { id } = useParams();

    const [product, setProduct] = useState({
        name: '',
        description: '',
        base_price: '',
        cost_price: '',
        stock_quantity: 0,
        total_sales_quantity: 0,
        is_customizable: '',
        assembly_required: '',
        measures: '',
        availability: '',
        image: '',
    });
    /** Remount rich text editor after load so Quill picks up HTML from the API. */
    const [descriptionEditorKey, setDescriptionEditorKey] = useState(0);

    const [file, setFile] = useState(null);
    const [fileError, setFileError] = useState('');
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [resultErrors, setResultErrors] = useState({
        name: null,
        description: null,
        basePrice: null,
        costPrice: null,
        is_customizable: null,
        assemblyRequired: null,
        measures: null,
        availability: null,
        image: null,
        has_errors: false
    });
    const [didNotLoad, setDidNotLoad] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [hasResponse, setHasResponse] = useState(false);
    const [costPrice, setCostPrice] = useState('');
    const [basePrice, setBasePrice] = useState('');
    const [costPriceFloat, setCostPriceFloat] = useState(undefined);
    const [basePriceFloat, setBasePriceFloat] = useState(undefined);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        setDidNotLoad(false);

        api.get(`products/${id}`)
            .then((response) => {
                if (cancelled) {
                    return;
                }
                try {
                    const payload = response?.data;
                    const data =
                        payload &&
                        typeof payload === 'object' &&
                        payload.data &&
                        typeof payload.data === 'object' &&
                        !Array.isArray(payload.data)
                            ? payload.data
                            : payload && typeof payload === 'object'
                              ? payload
                              : {};

                    const descRaw =
                        data.description ??
                        data.product_description ??
                        data.desc ??
                        '';

                    setProduct({
                        ...data,
                        description: descRaw != null ? String(descRaw) : '',
                        stock_quantity:
                            data.stock_quantity != null ? Number(data.stock_quantity) : 0,
                        total_sales_quantity:
                            data.total_sales_quantity != null ? Number(data.total_sales_quantity) : 0,
                        measures: measuresFromApiToForm(
                            data.measures && typeof data.measures === 'object' ? data.measures : null,
                        ),
                        availability:
                            data.availability && typeof data.availability === 'object'
                                ? data.availability
                                : { lead_time_for_order: '' },
                    });

                    const costCents = Math.max(0, Math.round(Number(data.cost_price) || 0));
                    const baseCents = Math.max(0, Math.round(Number(data.base_price) || 0));
                    setCostPrice('');
                    setBasePrice('');
                    setCostPriceFloat(costCents > 0 ? costCents / 100 : undefined);
                    setBasePriceFloat(baseCents > 0 ? baseCents / 100 : undefined);
                    setDescriptionEditorKey((k) => k + 1);
                } catch (error) {
                    console.log(error);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDidNotLoad(true);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [id]);

    const handleFileChange = (event) => {
        const file = event.target.files[0];

        if (file) {
            // Verifica o tipo de arquivo (JPG/PNG) e tamanho (máximo 2MB)
            if (file.size > 2 * 1024 * 1024) {
                setFileError("O arquivo não pode ser maior que 2MB.");
                return;
            }
            if (!["image/jpeg", "image/png"].includes(file.type)) {
                setFileError("Somente arquivos JPG ou PNG são permitidos.");
                return;
            }

            setFileError(""); // Limpa erro

            // Usar FileReader para ler o arquivo como base64
            const reader = new FileReader();

            reader.onloadend = () => {
                setFile({
                    name: file.name,
                    size: file.size,  // Armazenar o tamanho do arquivo
                    base64: reader.result
                });
            };

            reader.readAsDataURL(file); // Lê a imagem e converte para base64
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();

        let hasError = false
        if (product.name === '') {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors, // Mantém os valores existentes
                name: 'O nome do produto é obrigatório.', // Atualiza o campo 'brand'
                has_errors: true, // Atualiza o indicador de erro
            }));
        }

        if (!basePrice && (basePriceFloat == null || basePriceFloat === 0)) {
            hasError = true;
            setResultErrors((prevErrors) => ({
                ...prevErrors,
                base_price: 'O preço de venda é obrigatório.',
                has_errors: true,
            }));
        }

        if (hasError) {
            return;
        }

        if (isSubmitting) return; // Evita duplo clique
        setIsSubmitting(true); // Ativa o estado de loading

        let parsedMeasures = {};
        const toStored = (raw) => {
            if (raw === '' || raw == null) {
                return null;
            }
            const n = parseFloat(String(raw));
            if (!Number.isFinite(n)) {
                return null;
            }
            return Math.round(n * 100);
        };
        const h = toStored(product.measures?.height);
        const w = toStored(product.measures?.width);
        const len = toStored(product.measures?.length);
        if (h != null) {
            parsedMeasures.height = h;
        }
        if (w != null) {
            parsedMeasures.width = w;
        }
        if (len != null) {
            parsedMeasures.length = len;
        }

        const newProduct = {
            name: product.name,
            description: product.description || '',
            base_price: parseFormattedMoneyToCents(basePriceFloat, basePrice),
            cost_price: parseFormattedMoneyToCents(costPriceFloat, costPrice),
            stock_quantity: Math.max(0, parseInt(String(product.stock_quantity), 10) || 0),
            total_sales_quantity: Math.max(0, parseInt(String(product.total_sales_quantity), 10) || 0),
            is_customizable: product.is_customizable,
            assembly_required: product.assembly_required,
            measures: parsedMeasures,
            availability: product.availability || {}
        };
        if (file) {
            newProduct.image = file.base64;
        }

        api.put(`products/${id}`, newProduct)
            .then((response) => {
                console.log('Produto editado com sucesso:', response);
                navigate('/admin/products');  // Redireciona de volta para a lista de impressoras
                setHasResponse(true);
            })
            .catch((error) => {
                try {
                    const data = error.response.data;
                    setResultErrors({
                        ...data.errors,
                        has_errors: true,
                    });
                    setHasResponse(true);
                } catch (error) {
                    console.log(error);
                }
            })
            .finally(() => {
                setIsSubmitting(false); // Desativa o estado de loading
                if (!hasResponse) {
                    setDidNotLoad(true);
                }
            });
    };

    const handleRemoveFile = () => {
        setFile(null); // Reseta o estado do arquivo
        setFileError(''); // Opcional: Reseta também o erro, caso exista
    };

    const handleCustomizableChange = (event) => {
        const isChecked = event.target.checked; // Captura o estado do checkbox
        setProduct({ ...product, is_customizable: isChecked });
    };

    const handleAssemblyRequiredChange = (event) => {
        const isChecked = event.target.checked; // Captura o estado do checkbox
        setProduct({ ...product, assembly_required: isChecked });
    };

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <div>
            <Breadcrumbs aria-label="breadcrumb">
                <Link color="inherit" onClick={() => navigate('/admin')} to={'/admin'}>
                    Dashboard
                </Link>
                <Link color="inherit" onClick={() => navigate('/admin/products')} to={'/admin/products'}>
                    Produtos
                </Link>
                <Typography color="text.primary">Editando produto</Typography>
            </Breadcrumbs>
            <br />
            <Typography variant="h4" gutterBottom>Editar Produto</Typography>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Título do produto (obrigatório)"
                            fullWidth
                            value={product.name}
                            onChange={(e) => setProduct({ ...product, name: e.target.value })}
                        />
                        {resultErrors.name && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.name}
                            </div>
                        )}
                    </Grid>

                    <TextEditor
                        key={id}
                        contentRevision={descriptionEditorKey}
                        description={product.description ?? ''}
                        setDescription={(value) => {
                            setProduct((prev) => ({ ...prev, description: value }));
                        }}
                        error={resultErrors.description}
                    />
                    <Grid item xs={12} sm={12} mt={2}></Grid>
                    <Grid item xs={12} sm={12} mt={2}>
                        <Typography variant="h6">Imagem principal</Typography>
                        <input
                            type="file"
                            accept=".jpg, .jpeg, .png"
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                            id="file-upload"
                        />
                        {product.image ? (
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                mt={2}
                                mb={2}
                                p={2}
                                border="1px solid #ccc"
                                borderRadius="8px"
                                maxWidth="400px"
                            >
                                <Typography variant="subtitle1">Imagem Atual:</Typography>
                                <img
                                    src={resolvePublicMediaUrl(product.image)}
                                    alt="Imagem atual"
                                    style={{
                                        maxWidth: '400px',
                                        maxHeight: '400px',
                                        objectFit: 'contain',
                                        borderRadius: '8px',
                                        marginTop: '8px',
                                    }}
                                />
                            </Box>
                        ) : (
                            <Typography color="textSecondary" mt={2}>
                                Nenhuma imagem definida.
                            </Typography>
                        )}

                        <label htmlFor="file-upload">
                            <Button variant="contained" component="span" color="primary">
                                Alterar Imagem
                            </Button>
                        </label>

                        {fileError && <Typography color="error">{fileError}</Typography>}
                        {file && (
                            <>
                                <Typography mt={2}>
                                    Arquivo selecionado: {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)&nbsp;&nbsp;
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={handleRemoveFile}
                                        sx={{ mt: 1 }}
                                    >
                                        Remover Arquivo
                                    </Button>
                                </Typography>

                            </>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12} mt={2}>
                        <hr />
                        <Typography variant="h6">Precificação</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Custo de produção"
                            fullWidth
                            customInput={TextField}
                            value={costPriceFloat ?? costPrice}
                            onValueChange={(values) => {
                                setCostPrice(values.value);
                                setCostPriceFloat(values.floatValue);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                            inputMode="numeric"
                        />
                        {resultErrors.cost_price && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.cost_price}
                            </div>
                        )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <NumericFormat
                            label="Preço de venda (obrigatório)"
                            fullWidth
                            customInput={TextField}
                            value={basePriceFloat ?? basePrice}
                            onValueChange={(values) => {
                                setBasePrice(values.value);
                                setBasePriceFloat(values.floatValue);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                            inputMode="numeric"
                        />
                        {resultErrors.base_price && (
                            <div
                                className="text-danger mt-1"
                                style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem' }}
                            >
                                {resultErrors.base_price}
                            </div>
                        )}
                    </Grid>

                    <Grid item xs={12} sm={12} mt={2}>
                        <hr />
                        <Typography variant="h6">Medidas</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Largura (cm)"
                            fullWidth
                            type="number"
                            value={product?.measures?.width === '' || product?.measures?.width == null ? '' : product.measures.width}
                            onChange={(e) =>
                                setProduct({
                                    ...product,
                                    measures: {
                                        ...product.measures,
                                        width: e.target.value,
                                    },
                                })
                            }
                            inputMode="numeric"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Altura (cm)"
                            fullWidth
                            type="number"
                            value={product?.measures?.height === '' || product?.measures?.height == null ? '' : product.measures.height}
                            onChange={(e) =>
                                setProduct({
                                    ...product,
                                    measures: {
                                        ...product.measures,
                                        height: e.target.value,
                                    },
                                })
                            }
                            inputMode="numeric"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Profundidade (cm)"
                            fullWidth
                            type="number"
                            value={product?.measures?.length === '' || product?.measures?.length == null ? '' : product.measures.length}
                            onChange={(e) =>
                                setProduct({
                                    ...product,
                                    measures: {
                                        ...product.measures,
                                        length: e.target.value,
                                    },
                                })
                            }
                            inputMode="numeric"
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} mt={2}>
                        <hr />
                        <Typography variant="h6">Outras informações</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Estoque"
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: 1 }}
                            value={product.stock_quantity === '' || product.stock_quantity === undefined ? '' : product.stock_quantity}
                            onChange={(e) => {
                                const v = e.target.value;
                                setProduct({
                                    ...product,
                                    stock_quantity: v === '' ? '' : Math.max(0, parseInt(v, 10) || 0),
                                });
                            }}
                            inputMode="numeric"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Quantidade total vendida"
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: 1 }}
                            value={product.total_sales_quantity === '' || product.total_sales_quantity === undefined ? '' : product.total_sales_quantity}
                            onChange={(e) => {
                                const v = e.target.value;
                                setProduct({
                                    ...product,
                                    total_sales_quantity: v === '' ? '' : Math.max(0, parseInt(v, 10) || 0),
                                });
                            }}
                            helperText="Pode ser informada manualmente para histórico de vendas."
                            inputMode="numeric"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Dias necessários para produção"
                            fullWidth
                            type="number"
                            value={product?.availability?.lead_time_for_order ? product.availability.lead_time_for_order : ""}
                            onChange={(e) =>
                                setProduct({
                                    ...product,
                                    availability: {
                                        ...product.availability,
                                        lead_time_for_order: parseInt(e.target.value),
                                    },
                                })
                            }
                            inputMode="numeric"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={product.is_customizable}
                                    onChange={handleCustomizableChange}
                                    color="primary"
                                />
                            }
                            label="Produto customizável"
                        />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={product.assembly_required}
                                    onChange={handleAssemblyRequiredChange}
                                    color="primary"
                                />
                            }
                            label="Requer montagem"
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2, marginBottom: 20 }}>
                    <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isSubmitting} // Desativa o botão durante o envio
                        startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}
                        fullWidth
                    >
                        {isSubmitting ? 'Salvando...' : 'Gravar alterações'}
                    </Button>
                </Box>
            </form>
        </div>

    );
}

export default EditProductPage;
